import { ApplicationRef, createComponent, EnvironmentInjector, inject } from '@angular/core';
import { IframeComponent, WINDOW, WindowService } from '@campus/browser';
import {
  AuthService,
  CurrentExerciseActions,
  CurrentExerciseInterface,
  CurrentExerciseQueries,
  DalState,
  EduContent,
  EduContentInterface,
  LinkedPersonQueries,
  ResultActions,
  ResultInterface,
  ResultStatusEnum,
  ScormCmiMode,
} from '@campus/dal';
import { ENVIRONMENT_API_TOKEN } from '@campus/environment';
import {
  CustomExerciseServiceInterface,
  ExerciseConfiguration,
  NormalExerciseConfigurationInterface,
  ReviewExerciseConfigurationInterface,
} from '@campus/exercise';
import { select, Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { DialogReviewComponent } from '../components/dialog-review/dialog-review.component';
import { GetResultResponseWebSocketMessage, LinguineoEndDialogEvent, SpeechResultMessage } from '../interfaces';

export class SpeechExerciseService implements CustomExerciseServiceInterface {
  private windowService = inject(WindowService);
  private window = inject(WINDOW);
  private environmentApi = inject(ENVIRONMENT_API_TOKEN);
  private store = inject<Store<DalState>>(Store);
  private auth = inject(AuthService);
  private appRef = inject(ApplicationRef);
  private envInjector = inject(EnvironmentInjector);

  isContentCompatible(content: EduContentInterface): boolean {
    const eduContent = content instanceof EduContent ? content : EduContent.toEduContent(content);
    return eduContent.isSpeech;
  }

  shouldGetResult(data: ExerciseConfiguration): boolean {
    return data.mode === ScormCmiMode.CMI_MODE_REVIEW;
  }

  loadExercise(data: ExerciseConfiguration): void {
    const { mode, eduContent } = data;

    if (mode === ScormCmiMode.CMI_MODE_REVIEW) {
      this.openReview(data as ReviewExerciseConfigurationInterface);
      return;
    }

    // Teacher preview and Browse mode must not POST a Result — the endpoint rejects
    // non-student users with 401.
    const isNormalMode = mode === ScormCmiMode.CMI_MODE_NORMAL;
    const localSession =
      isNormalMode && (data as NormalExerciseConfigurationInterface).playerOptions?.localSession === true;
    const saveToApi = isNormalMode && !localSession;

    this.store
      .pipe(
        select(CurrentExerciseQueries.getCurrentExercise),
        filter((ex) => !!ex?.url && ex.eduContentId === eduContent.id),
        take(1)
      )
      .subscribe(({ url }) => {
        const dialogData = { url, onPostMessage: this.onPostMessage.bind(this), allow: ['microphone'] };
        this.windowService.openDialog(IframeComponent, { data: dialogData, panelClass: 'ui-iframe--fullscreen' });
      });

    this.store.dispatch(
      new CurrentExerciseActions.LoadExercise({
        userId: this.auth.userId,
        eduContentId: eduContent.id,
        saveToApi,
        cmiMode: mode,
        context: data.context,
      })
    );
  }

  private openReview(data: ReviewExerciseConfigurationInterface): void {
    const resultId = data.result?.id;
    if (resultId == null) return;
    const personId = data.result?.personId;

    const host = this.window.document.createElement('div');
    this.window.document.body.appendChild(host);

    const ref = createComponent(DialogReviewComponent, {
      environmentInjector: this.envInjector,
      hostElement: host,
    });
    ref.setInput('resultId', resultId);

    if (personId) {
      this.store.pipe(select(LinkedPersonQueries.getById, { id: personId }), take(1)).subscribe((person) => {
        if (person) {
          ref.setInput('student', {
            firstName: person.firstName,
            lastName: person.name,
            displayName: person.displayName,
            avatar: person.avatar,
          });
        }
      });
    }

    ref.instance.closed.subscribe(() => {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
      host.remove();
    });
    this.appRef.attachView(ref.hostView);
  }

  private onPostMessage(e: MessageEvent): void {
    // Local dev serves the iframe from APIBase while the host runs on localhost,
    // so both origins are accepted.
    const apiOrigin = new URL(this.environmentApi.APIBase, this.window.location.href).origin;
    if (e.origin !== this.window.location.origin && e.origin !== apiOrigin) return;

    const message = e.data as Partial<SpeechResultMessage> | undefined;
    if (message?.type !== 'speech:result' || !message.data) return;

    this.onSpeechResult(message.data, message.dialogEndDetail);
  }

  private onSpeechResult(
    speechResult: GetResultResponseWebSocketMessage,
    dialogEndDetail?: LinguineoEndDialogEvent['detail']
  ): void {
    this.store.pipe(select(CurrentExerciseQueries.getCurrentExercise), take(1)).subscribe((currentExercise) => {
      if (!currentExercise) return;
      const updatedResult = this.buildResult(currentExercise.result, speechResult, dialogEndDetail);
      this.dispatchExercise({ ...currentExercise, result: updatedResult });
    });
  }

  // Keep speechResult in cmi; persist conversationId so a teacher review can later
  // fetch the full transcript via Linguineo /personalities/conversations/:id/detail.
  private buildResult(
    base: ResultInterface,
    speechResult: GetResultResponseWebSocketMessage,
    dialogEndDetail?: LinguineoEndDialogEvent['detail']
  ): ResultInterface {
    return {
      ...base,
      cmi: JSON.stringify({ speechResult, conversationId: dialogEndDetail?.conversationId }),
      // Score is dropped server-side unless EduContentMetadata.quotable is true.
      score: Math.round(speechResult.data.result.score.accuracy * 100),
      status: ResultStatusEnum.STATUS_COMPLETED,
      time: dialogEndDetail?.durationInMs ?? 0,
    };
  }

  private dispatchExercise(currentExercise: CurrentExerciseInterface): void {
    this.store.dispatch(
      new CurrentExerciseActions.SaveCurrentExercise({
        userId: currentExercise.result.personId,
        exercise: currentExercise,
      })
    );

    // Set temporary completion values so the UI updates before the API response arrives.
    if (currentExercise.saveToApi) {
      this.store.dispatch(
        new ResultActions.UpsertResult({
          result: {
            ...currentExercise.result,
            status: ResultStatusEnum.STATUS_COMPLETED,
            lastUpdated: new Date(),
          },
        })
      );
    }
  }
}
